// pages/OpenLeaks/OpenLeaks.tsx (updated with video background)
import React, { useContext, useEffect, useState, useRef } from "react";
import { OpenLeakContext } from "features/openleak";
import { LeakFilters } from "features/openleak";
import OpenLeaksTimeline from './components/OpenLeaksTimeline/OpenLeaksTimeline';
import LeakFiltersComponent from './components/LeakFilters/LeakFilters';
import LeakStats from './components/LeakStats/LeakStats';
import FollowAlert from './components/FollowAlert/FollowAlert';
import FilterModal from './components/FilterModal/FilterModal';
import { ReactComponent as IconRefresh } from 'assets/img/icons/icon_refresh.svg';
import { ReactComponent as IconFilter } from 'assets/img/icons/icon_Filter.svg';
import { ReactComponent as IconNext } from 'assets/img/icons/icon_arrow-Right.svg';
import { ReactComponent as IconNotif } from 'assets/img/icons/icon_bell.svg';
import { ReactComponent as IconBack } from "assets/img/icons/icon_arrow-Left.svg";
import { ReactComponent as IconAlert } from "assets/img/icons/icon_Warning.svg";
import { ReactComponent as IconShield } from "assets/img/icons/icon_shield-Locked.svg";
import './OpenLeaks.scss';
import { useNavigate } from "react-router-dom";
import { UserContext } from "features/account";
import { getSubscription } from "features/account";
import Button from "shared/ui/Button";
import { Button_Style, Button_Type } from "shared/ui/Button/Button";

const backgroundVideo = require("assets/videos/DataLeak.mp4");

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const OpenLeaksPage: React.FC = () => {
  const {
    leaks,
    totalLeaks,
    loading,
    error,
    filters,
    setFilters,
    resetFilters,
    fetchLeaks,
    stats
  } = useContext(OpenLeakContext);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const type = user?.type;

  const [hasOffer, sethasOffer] = useState<any>(null);

  useEffect(() => {
    const checkOffer = async () => {
      try {
        const response = await getSubscription(type, user?.email);
        if (response.data.offrePurchase && response.data.offrePurchase.id > 1) {
          sethasOffer(true);
        }
        else{
          sethasOffer(false);
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (user) {
      checkOffer();
    }
  }, [user, type]);

  const activeIDProtectDarkWeb = () => {
    if (user?.type) {
      navigate(`${type === "partner" ? "/professionnels" : "/particuliers"}/id-tracker-pricing`)
    } else {
      navigate(`/particuliers/login`)
    }
  }

  // Appliquer les filtres
  const handleFilterChange = (newFilters: LeakFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  // Réinitialiser les filtres
  const handleResetFilters = () => {
    resetFilters();
    setIsFilterModalOpen(false);
  };

  useEffect(() => {
    document.body.classList.add("open-leaks-page");
    return () => {
      document.body.classList.remove("open-leaks-page");
    };
  }, []);

  // Rafraîchir les données
  const handleRefresh = () => {
    fetchLeaks();
  };

  // Activer la surveillance
  const handleActivateSurveillance = () => {
    console.log('Activer la surveillance clicked');
  };

  // Video load handler
  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay failed:', err);
      });
    }
  };

  return (
    <div className="open-leaks-page">
      {/* Sticky Top Bar */}
      <div className="open-leaks-topbar">
        <div className="open-leaks-topbar__container">
          <div className="open-leaks-topbar__logo">
            <span className="logo-text">ID Protect</span>
            <>
              <div className={"text-gray-500"}>|</div>
              <div className={"text-muted-foreground text-xs"}>Quelle entreprise a été victime d'une fuite aujourd'hui ?</div>
            </>
          </div>

          <div className="open-leaks-topbar__actions">
            <>
              {hasOffer === false ? (
                <div
                  className={`shadow show tag-icon tag-error rounded-lg d-flex flex-row justify-content-center align-items-center ${isMobile ? 'IDTrackerMobile': 'IDTrackerWeb'}`}>
                  <IconAlert
                    fill={"#ffffff"}
                    height={20}
                    className="PageHeader__icon"
                    width={20}
                  />
                  <span className={"text-sm bold"}>Protection Dark Web Inactive</span>
                  <Button
                    label={"ACTIVER"}
                    type={Button_Type.WHITE}
                    style={Button_Style.SOLID} className={"rounded-lg mb-1 shadow"}
                    onClick={activeIDProtectDarkWeb}
                  />
                </div>
              ): (
                <div
                  className={`shadow show tag-icon tag-success rounded-lg d-flex flex-row flex-wrap justify-content-center align-items-center ${isMobile ? 'IDTrackerMobile': 'IDTrackerWeb'}`}>
                  <IconShield
                    fill={"#148c48"}
                    height={20}
                    className="PageHeader__icon"
                    width={20}
                  />
                  <span className={"text-sm bold text-primary pr-2"}>Protection Dark Web <span
                    className={"text-success"}>Activée</span></span>
                </div>
              )}
            </>
          </div>
        </div>
      </div>

      {/* Hero Section with Video Background */}
      <div className="open-leaks-hero">
        {/* Video Background */}
        {!isMobile && (
          <video
            ref={videoRef}
            className={`hero-video-background ${videoLoaded ? 'video-loaded' : ''}`}
            autoPlay
            loop
            muted
            playsInline
            poster=""
            onLoadedData={handleVideoLoaded}
          >
            <source src={backgroundVideo} type="video/mp4" />
          </video>
        )}

        {/* Gradient Overlay */}
        <div className="hero-gradient-overlay"></div>

        {/* Content */}
        <div className="hero-content">
          <div>
            <h1 className="open-leaks-hero__title">
              Surveillance des fuites de données
            </h1>
          </div>
          {/* Stats Section */}
          {stats && (
            <div className="open-leaks-stats">
            <LeakStats stats={stats} />
            </div>
          )}
        </div>
      </div>

      {/* Actions Bar */}
      <div className="open-leaks-actions">
        <div className="open-leaks-actions__left">
          <button
            className="action-btn action-btn--filter"
            onClick={() => setIsFilterModalOpen(true)}
          >
            <IconFilter className="action-btn__icon" fill={"#fff"} />
            <span>Filtrer</span>
          </button>

          {(filters.search || filters.status || filters.startDate || filters.endDate) && (
            <button
              className="action-btn action-btn--reset"
              onClick={handleResetFilters}
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>

        <div className="open-leaks-actions__right">
          <div className="leaks-count-badge">
            <span className="leaks-count-number">{totalLeaks}</span>
            <span className="leaks-count-label">fuite{totalLeaks > 1 ? 's' : ''}</span>
          </div>

          <button
            className="action-btn action-btn--refresh"
            onClick={handleRefresh}
            disabled={loading}
          >
            <IconRefresh className={`action-btn__icon ${loading ? 'spin' : ''}`} />
            <span>Rafraîchir</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="open-leaks-error">
          <span className="error-text">{error}</span>
          <button className="error-dismiss" onClick={() => {}}>×</button>
        </div>
      )}

      {/* Timeline View */}
      {loading && leaks.length === 0 && (
        <div className="timeline-loading">
          <div className="loader"></div>
          <p>Chargement de la chronologie...</p>
        </div>
      )}

      {!loading && leaks.length === 0 && !error && (
        <div className="open-leaks-empty">
          <div className="empty-icon">🔍</div>
          <h3 className="empty-title">Aucune fuite trouvée</h3>
          <p className="empty-description">
            Aucune fuite ne correspond à vos critères de recherche.
            Essayez de modifier vos filtres.
          </p>
          <button className="empty-reset" onClick={handleResetFilters}>
            Réinitialiser les filtres
          </button>
        </div>
      )}

      {!loading && leaks.length > 0 && (
        <OpenLeaksTimeline leaks={leaks} loading={loading} />
      )}

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
      />
    </div>
  );
};

export default OpenLeaksPage;